// src/pages/CheckinReview.js
//
// Two modes:
//   - "live" mode: conversation just finished, two-step reveal flow
//   - "history" mode: completed conversation being re-read, both answers visible
//
// At the end of live mode: capture commitment.
// In history mode: show the commitment that was captured, with status.
//
import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { theme } from '../lib/theme'

const c = theme.colors
const f = theme.fonts

const formatDate = (dateString) => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric',
  })
}

export default function CheckinReview() {
  const { id: conversationId } = useParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState(null)
  const [conversation, setConversation] = useState(null)
  const [partnerName, setPartnerName] = useState('your partner')
  const [questions, setQuestions] = useState([])
  const [currentIdx, setCurrentIdx] = useState(0)
  const [revealed, setRevealed] = useState({})
  const [step, setStep] = useState('reviewing')
  const [commitmentText, setCommitmentText] = useState('')
  const [agreedByBoth, setAgreedByBoth] = useState(true)
  const [saving, setSaving] = useState(false)
  const [historicalCommitment, setHistoricalCommitment] = useState(null)

  // Detect mode based on conversation status
  const mode = conversation?.status === 'completed' ? 'history' : 'live'

  useEffect(() => { fetchAll() }, [conversationId])

  async function fetchAll() {
    setLoading(true)
    const { data: { user: u } } = await supabase.auth.getUser()
    if (!u) { setLoading(false); return }
    setUser(u)

    const { data: convo } = await supabase.from('money_conversations')
      .select('*')
      .eq('id', conversationId)
      .maybeSingle()
    if (!convo) {
      navigate('/checkins')
      return
    }
    setConversation(convo)

    // Get partner name
    const { data: link } = await supabase.from('partner_links')
      .select('user_id_1, user_id_2')
      .eq('id', convo.partnership_id)
      .maybeSingle()
    if (link) {
      const partnerId = link.user_id_1 === u.id ? link.user_id_2 : link.user_id_1
      const { data: pp } = await supabase.from('profiles')
        .select('full_name')
        .eq('id', partnerId)
        .maybeSingle()
      if (pp?.full_name) {
        setPartnerName(pp.full_name.split(' ')[0])
      }
    }

    // Fetch questions + responses
    const { data: questionRows } = await supabase.from('conversation_questions')
      .select('id, custom_text, sort_order, question_library(text)')
      .eq('conversation_id', conversationId)
      .order('sort_order', { ascending: true })

    const cqIds = (questionRows || []).map(q => q.id)
    const { data: allResponses } = await supabase.from('conversation_responses')
      .select('*')
      .in('conversation_question_id', cqIds)

    const flatQuestions = (questionRows || []).map(q => {
      const yourResp = (allResponses || []).find(r =>
        r.conversation_question_id === q.id && r.user_id === u.id
      )
      const partnerResp = (allResponses || []).find(r =>
        r.conversation_question_id === q.id && r.user_id !== u.id
      )
      return {
        id: q.id,
        text: q.custom_text || q.question_library?.text || '(no text)',
        yourResponse: yourResp,
        partnerResponse: partnerResp,
      }
    })
    setQuestions(flatQuestions)

    // For history mode: fetch the commitment that was made
    if (convo.status === 'completed') {
      const { data: commitments } = await supabase.from('conversation_commitments')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: false })
      if (commitments && commitments.length > 0) {
        setHistoricalCommitment(commitments[0])
      }
    }

    setLoading(false)
  }

  const reveal = () => {
    const cq = questions[currentIdx]
    if (!cq) return
    setRevealed(prev => ({ ...prev, [cq.id]: true }))
  }

  const goNext = () => {
    if (currentIdx < questions.length - 1) {
      setCurrentIdx(currentIdx + 1)
    } else {
      setStep('commitment')
    }
  }

  const goPrev = () => {
    if (currentIdx > 0) {
      setCurrentIdx(currentIdx - 1)
    }
  }

  const finishConversation = async () => {
    setSaving(true)

    if (commitmentText.trim()) {
      const targetDate = new Date()
      targetDate.setMonth(targetDate.getMonth() + 1)
      await supabase.from('conversation_commitments').insert({
        conversation_id: conversationId,
        text: commitmentText.trim(),
        agreed_by_both: agreedByBoth,
        target_date: targetDate.toISOString().split('T')[0],
        status: 'active',
      })
    }

    await supabase.from('money_conversations')
      .update({
        status: 'completed',
        completed_at: new Date().toISOString(),
      })
      .eq('id', conversationId)

    setSaving(false)
    navigate('/checkins')
  }

  if (loading) {
    return <div style={styles.loading}>Loading…</div>
  }

  if (questions.length === 0) {
    return (
      <div style={styles.page}>
        <p style={styles.errorText}>No questions in this conversation.</p>
      </div>
    )
  }

  // ──────────────────────────────────────────────────────
  // HISTORY MODE — re-reading a completed conversation
  // ──────────────────────────────────────────────────────
  if (mode === 'history') {
    return (
      <div style={styles.page}>
        <header style={styles.historyHeader}>
          <button onClick={() => navigate('/checkins')} style={styles.exitBtn}>
            ‹ Back to Check-ins
          </button>
          <div style={styles.historyMeta}>
            <span style={styles.historyDate}>
              {formatDate(conversation.completed_at)}
            </span>
            <span style={styles.historyDot}>·</span>
            <span style={styles.historyMode}>
              {conversation.mode === 'sync' ? '🤝 Sync' : '⏳ Async'}
            </span>
          </div>
          <h1 style={styles.historyTitle}>
            Conversation with {partnerName}
          </h1>
        </header>

        <div style={styles.historyQuestions}>
          {questions.map((q, idx) => (
            <HistoryQuestion
              key={q.id}
              questionNum={idx + 1}
              question={q}
              partnerName={partnerName}
            />
          ))}
        </div>

        {historicalCommitment && (
          <CommitmentDisplay commitment={historicalCommitment} />
        )}

        <div style={styles.historyFooter}>
          <button
            onClick={() => navigate('/checkins')}
            style={styles.secondaryBtn}
          >
            Back to Check-ins
          </button>
        </div>
      </div>
    )
  }

  // ──────────────────────────────────────────────────────
  // COMMITMENT STEP (live mode only)
  // ──────────────────────────────────────────────────────
  if (step === 'commitment') {
    return (
      <div style={styles.page}>
        <header style={styles.header}>
          <button onClick={() => setStep('reviewing')} style={styles.exitBtn}>
            ‹ Back to review
          </button>
        </header>

        <div style={styles.commitmentCard}>
          <h1 style={styles.commitmentTitle}>One small thing to try</h1>
          <p style={styles.commitmentSubtitle}>
            Pick one thing — concrete and small — that you'll try together this month.
            It might be from one of your answers, or something new that came up.
          </p>
          <p style={styles.commitmentHint}>
            Optional — leave blank if you'd rather just have the conversation.
          </p>

          <textarea
            value={commitmentText}
            onChange={(e) => setCommitmentText(e.target.value)}
            placeholder="e.g. We'll talk before any non-essential purchase over $200..."
            style={styles.commitmentTextarea}
            rows={4}
          />

          {commitmentText.trim() && (
            <label style={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={agreedByBoth}
                onChange={(e) => setAgreedByBoth(e.target.checked)}
                style={styles.checkbox}
              />
              <span>Both partners agreed to this</span>
            </label>
          )}

          <div style={styles.commitmentActions}>
            <button
              onClick={() => {
                setCommitmentText('')
                finishConversation()
              }}
              style={styles.skipCommitmentBtn}
            >
              Skip — just finish
            </button>
            <button
              onClick={finishConversation}
              disabled={saving}
              style={{
                ...styles.primaryBtn,
                opacity: saving ? 0.6 : 1,
                cursor: saving ? 'wait' : 'pointer',
              }}
            >
              {saving ? 'Saving…' : commitmentText.trim() ? 'Save commitment & finish' : 'Finish'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ──────────────────────────────────────────────────────
  // LIVE MODE — two-step reveal
  // ──────────────────────────────────────────────────────
  const current = questions[currentIdx]
  const totalQuestions = questions.length
  const progress = Math.round(((currentIdx + 1) / totalQuestions) * 100)
  const isRevealed = !!revealed[current.id]
  const isLastQuestion = currentIdx === totalQuestions - 1

  const yourSkipped = current.yourResponse?.skipped
  const partnerSkipped = current.partnerResponse?.skipped

  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <button onClick={() => navigate('/checkins')} style={styles.exitBtn}>
          ‹ Save & exit
        </button>
        <div style={styles.progressWrap}>
          <div style={styles.progressBarBg}>
            <div style={{ ...styles.progressBarFill, width: `${progress}%` }} />
          </div>
          <div style={styles.progressText}>
            Question {currentIdx + 1} of {totalQuestions}
          </div>
        </div>
      </header>

      <div style={styles.questionText}>{current.text}</div>

      <div style={styles.answerBlock}>
        <div style={styles.answerLabel}>You</div>
        <div style={styles.answerCard}>
          {yourSkipped ? (
            <span style={styles.skippedText}>You skipped this question</span>
          ) : (
            <span style={styles.answerText}>
              {current.yourResponse?.answer_text || <em style={styles.noAnswer}>No answer</em>}
            </span>
          )}
        </div>
      </div>

      <div style={styles.answerBlock}>
        <div style={styles.answerLabel}>{partnerName}</div>
        {!isRevealed ? (
          <button onClick={reveal} style={styles.revealBtn}>
            <span>Reveal {partnerName}'s answer</span>
            <span style={styles.revealHint}>Tap when you're ready</span>
          </button>
        ) : (
          <div style={{ ...styles.answerCard, ...styles.partnerCard }}>
            {partnerSkipped ? (
              <span style={styles.skippedText}>{partnerName} skipped this question</span>
            ) : (
              <span style={styles.answerText}>
                {current.partnerResponse?.answer_text || <em style={styles.noAnswer}>No answer</em>}
              </span>
            )}
          </div>
        )}
      </div>

      <div style={styles.actionsRow}>
        <button
          onClick={goPrev}
          disabled={currentIdx === 0}
          style={{
            ...styles.secondaryBtn,
            opacity: currentIdx === 0 ? 0.4 : 1,
            cursor: currentIdx === 0 ? 'not-allowed' : 'pointer',
          }}
        >
          ‹ Previous
        </button>
        <button
          onClick={goNext}
          disabled={!isRevealed}
          style={{
            ...styles.primaryBtn,
            opacity: !isRevealed ? 0.5 : 1,
            cursor: !isRevealed ? 'not-allowed' : 'pointer',
          }}
        >
          {isLastQuestion ? (
            'Capture commitment'
          ) : (
            <>Next question ›</>
          )}
        </button>
      </div>

      {!isRevealed && (
        <div style={styles.bottomMeta}>
          <span>Take a moment with your own answer first</span>
        </div>
      )}
    </div>
  )
}

// ──────────────────────────────────────────────────────────────
// History mode — single question with both answers visible
// ──────────────────────────────────────────────────────────────
function HistoryQuestion({ questionNum, question, partnerName }) {
  return (
    <div style={styles.historyQuestionCard}>
      <div style={styles.historyQuestionNum}>Question {questionNum}</div>
      <div style={styles.historyQuestionText}>{question.text}</div>

      <div style={styles.historyAnswers}>
        <div style={styles.historyAnswer}>
          <div style={styles.answerLabel}>You</div>
          <div style={styles.historyAnswerText}>
            {question.yourResponse?.skipped ? (
              <span style={styles.skippedText}>You skipped this question</span>
            ) : (
              question.yourResponse?.answer_text || <em style={styles.noAnswer}>No answer</em>
            )}
          </div>
        </div>

        <div style={styles.historyAnswer}>
          <div style={styles.answerLabel}>{partnerName}</div>
          <div style={{ ...styles.historyAnswerText, ...styles.partnerHistoryAnswer }}>
            {question.partnerResponse?.skipped ? (
              <span style={styles.skippedText}>{partnerName} skipped this question</span>
            ) : (
              question.partnerResponse?.answer_text || <em style={styles.noAnswer}>No answer</em>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ──────────────────────────────────────────────────────────────
// Commitment display in history mode
// ──────────────────────────────────────────────────────────────
function CommitmentDisplay({ commitment }) {
  let statusText, statusColor, statusBg
  switch (commitment.status) {
    case 'completed':
      statusText = 'Completed ✓'
      statusColor = c.green
      statusBg = c.greenSoft || '#e8f5e9'
      break
    case 'struggled':
      statusText = 'Struggled'
      statusColor = '#cc7a00'
      statusBg = 'rgba(204, 122, 0, 0.08)'
      break
    case 'expired':
      statusText = 'Expired (no follow-up)'
      statusColor = c.inkMuted
      statusBg = c.cream2 || '#faf8f5'
      break
    default:
      statusText = 'Active'
      statusColor = c.green
      statusBg = c.greenSoft || '#e8f5e9'
  }

  return (
    <div style={{
      ...styles.commitmentDisplay,
      background: statusBg,
      borderColor: statusColor,
    }}>
      <div style={styles.commitmentDisplayBody}>
        <div style={styles.commitmentDisplayLabel}>The commitment</div>
        <div style={styles.commitmentDisplayText}>{commitment.text}</div>
        <div style={{ ...styles.commitmentDisplayStatus, color: statusColor }}>
          {statusText}
          {commitment.agreed_by_both && commitment.status !== 'expired' && (
            <span style={styles.bothAgreed}> · Both agreed</span>
          )}
        </div>
      </div>
    </div>
  )
}

const styles = {
  page: { padding: '24px', maxWidth: '740px', margin: '0 auto' },
  loading: { padding: '40px', textAlign: 'center', color: c.inkMuted },
  errorText: { color: c.inkMuted, fontSize: '14px', textAlign: 'center', padding: '40px' },

  header: { marginBottom: '24px' },
  exitBtn: {
    background: 'none', border: 'none',
    color: c.inkMuted, fontSize: '13px',
    cursor: 'pointer', padding: '4px 0',
    display: 'inline-flex', alignItems: 'center', gap: '2px',
    marginBottom: '14px', fontFamily: 'inherit',
  },
  progressWrap: { display: 'flex', flexDirection: 'column', gap: '6px' },
  progressBarBg: {
    width: '100%', height: '6px',
    background: c.line, borderRadius: '3px',
    overflow: 'hidden',
  },
  progressBarFill: {
    height: '100%', background: c.green,
    borderRadius: '3px',
    transition: 'width 220ms ease-out',
  },
  progressText: { fontSize: '12px', color: c.inkMuted, fontWeight: 500 },

  questionText: {
    fontFamily: f.display, fontSize: '24px', fontWeight: 600,
    color: c.plum, lineHeight: 1.35,
    marginBottom: '24px',
    letterSpacing: '-0.015em',
  },

  answerBlock: { marginBottom: '20px' },
  answerLabel: {
    fontSize: '11px', fontWeight: 600,
    color: c.inkMuted, textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginBottom: '8px',
  },
  answerCard: {
    background: c.white, border: `1px solid ${c.line}`,
    borderRadius: '12px', padding: '20px',
    minHeight: '70px',
  },
  partnerCard: {
    background: c.greenSoft || '#e8f5e9',
    borderColor: c.green,
    animation: 'fadeIn 320ms ease-out',
  },
  answerText: {
    fontSize: '15px', lineHeight: 1.55,
    color: c.ink, whiteSpace: 'pre-wrap',
  },
  noAnswer: { color: c.inkMuted, fontStyle: 'italic' },
  skippedText: { color: c.inkMuted, fontStyle: 'italic', fontSize: '14px' },

  revealBtn: {
    width: '100%',
    background: c.cream2 || '#faf8f5',
    border: `2px dashed ${c.line}`,
    borderRadius: '12px',
    padding: '24px 20px',
    cursor: 'pointer',
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', gap: '6px',
    fontFamily: 'inherit',
    color: c.plum,
    fontWeight: 600, fontSize: '15px',
  },
  revealHint: {
    fontSize: '12px', color: c.inkMuted, fontWeight: 400,
    fontStyle: 'italic',
  },

  actionsRow: {
    display: 'flex', gap: '10px', alignItems: 'center',
    marginTop: '24px',
  },
  primaryBtn: {
    background: c.green, color: c.white,
    border: 'none', borderRadius: '10px',
    padding: '12px 20px', fontSize: '14px', fontWeight: 600,
    cursor: 'pointer', fontFamily: 'inherit',
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    flex: 1, justifyContent: 'center',
  },
  secondaryBtn: {
    background: c.white, color: c.inkMuted,
    border: `1px solid ${c.line}`, borderRadius: '10px',
    padding: '12px 16px', fontSize: '13px', fontWeight: 500,
    cursor: 'pointer', fontFamily: 'inherit',
    display: 'inline-flex', alignItems: 'center', gap: '4px',
  },

  bottomMeta: {
    display: 'flex', justifyContent: 'center',
    fontSize: '12px', color: c.inkMuted,
    fontStyle: 'italic',
    padding: '12px 4px',
  },

  commitmentCard: {
    background: c.white, borderRadius: '16px',
    border: `1px solid ${c.line}`,
    padding: '32px 28px',
    textAlign: 'center',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px',
  },
  commitmentIcon: {
    width: '52px', height: '52px',
    borderRadius: '50%',
    background: c.greenSoft || '#e8f5e9',
    color: c.green,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    marginBottom: '4px',
  },
  commitmentTitle: {
    fontFamily: f.display, fontSize: '24px', fontWeight: 600,
    color: c.plum, margin: 0, letterSpacing: '-0.015em',
  },
  commitmentSubtitle: {
    fontSize: '15px', color: c.ink,
    lineHeight: 1.5, maxWidth: '460px', margin: 0,
  },
  commitmentHint: {
    fontSize: '12px', color: c.inkMuted, fontStyle: 'italic',
    margin: 0,
  },
  commitmentTextarea: {
    width: '100%',
    border: `1px solid ${c.line}`,
    borderRadius: '10px',
    padding: '14px',
    fontSize: '15px', lineHeight: 1.5,
    fontFamily: 'inherit',
    background: c.cream2 || '#faf8f5',
    outline: 'none', resize: 'vertical',
    marginTop: '8px',
  },
  checkboxRow: {
    display: 'inline-flex', alignItems: 'center', gap: '8px',
    fontSize: '13px', color: c.ink,
    cursor: 'pointer',
    marginTop: '4px',
  },
  checkbox: { cursor: 'pointer' },
  commitmentActions: {
    display: 'flex', gap: '10px', width: '100%',
    marginTop: '12px',
  },
  skipCommitmentBtn: {
    background: 'none', color: c.inkMuted,
    border: `1px solid ${c.line}`, borderRadius: '10px',
    padding: '12px 16px', fontSize: '13px', fontWeight: 500,
    cursor: 'pointer', fontFamily: 'inherit',
  },

  // ── History mode styles ────────────────────────────────
  historyHeader: {
    marginBottom: '28px',
  },
  historyMeta: {
    display: 'inline-flex', alignItems: 'center', gap: '8px',
    fontSize: '12px', color: c.inkMuted,
    marginBottom: '6px',
  },
  historyDate: { fontFamily: 'inherit' },
  historyDot: { color: c.line },
  historyMode: {},
  historyTitle: {
    fontFamily: f.display, fontSize: '28px', fontWeight: 600,
    color: c.plum, margin: 0, letterSpacing: '-0.015em',
  },

  historyQuestions: {
    display: 'flex', flexDirection: 'column', gap: '14px',
  },
  historyQuestionCard: {
    background: c.white, borderRadius: '14px',
    border: `1px solid ${c.line}`,
    padding: '20px 22px',
  },
  historyQuestionNum: {
    fontSize: '11px', fontWeight: 600,
    color: c.inkMuted, textTransform: 'uppercase',
    letterSpacing: '0.04em', marginBottom: '6px',
  },
  historyQuestionText: {
    fontFamily: f.display, fontSize: '17px', fontWeight: 600,
    color: c.plum, lineHeight: 1.4,
    marginBottom: '16px',
    letterSpacing: '-0.01em',
  },
  historyAnswers: {
    display: 'flex', flexDirection: 'column', gap: '12px',
  },
  historyAnswer: {},
  historyAnswerText: {
    background: c.cream2 || '#faf8f5',
    border: `1px solid ${c.line}`,
    borderRadius: '10px',
    padding: '14px 16px',
    fontSize: '14px', lineHeight: 1.5,
    color: c.ink, whiteSpace: 'pre-wrap',
  },
  partnerHistoryAnswer: {
    background: c.greenSoft || '#e8f5e9',
    borderColor: c.green,
  },

  commitmentDisplay: {
    border: `1px solid`, borderRadius: '14px',
    padding: '18px 22px',
    marginTop: '20px',
    display: 'flex', alignItems: 'flex-start', gap: '14px',
  },
  commitmentDisplayIcon: {
    width: '36px', height: '36px',
    borderRadius: '50%',
    background: c.white,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  commitmentDisplayBody: { flex: 1, minWidth: 0 },
  commitmentDisplayLabel: {
    fontSize: '11px', fontWeight: 600,
    color: c.inkMuted, textTransform: 'uppercase',
    letterSpacing: '0.04em', marginBottom: '4px',
  },
  commitmentDisplayText: {
    fontSize: '15px', color: c.plum, fontWeight: 500,
    lineHeight: 1.45, marginBottom: '6px',
  },
  commitmentDisplayStatus: {
    fontSize: '12.5px', fontWeight: 600,
  },
  bothAgreed: {
    fontWeight: 400, fontStyle: 'italic',
  },

  historyFooter: {
    marginTop: '24px',
    display: 'flex', justifyContent: 'flex-end',
  },
}

if (typeof document !== 'undefined' && !document.getElementById('checkin-review-keyframes')) {
  const style = document.createElement('style')
  style.id = 'checkin-review-keyframes'
  style.textContent = `
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(4px); }
      to { opacity: 1; transform: translateY(0); }
    }
  `
  document.head.appendChild(style)
}